import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getArticleTypeList } from "services/article-types.services";
import {
  createArticle,
  getArticleDetailsById,
  updateArticleDetailsById,
} from "services/article.services";
import { getCompositionList } from "services/composition.services";
import { getGenders } from "services/gender.services";
import { getTemplates } from "services/template.services";
import {
  setArticleDetails,
  setArticleList,
  toggleActionableArticleId,
  toggleDuplicateAction,
  toggleIsArticleCreateModalOpen,
  toggleIsArticleEditable,
  toggleShowDiscardModal,
} from "store/articleSlice";
import { setRefetchBillingData } from "store/billingSlice";
import { setGenderList } from "store/genderSlice";
import { articleCompositionsList, iconComponents } from "utils/fakeData";
import { formateLable, removeQueryParam } from "utils/helper";
import { createArticleSchema } from "validation/article.validation";
import useCustomToaster from "./useCustomToaster";
import { Button } from "@mui/material";
import { setFormatedArticleList, setShowArticleForm } from "store/orderSlice";

const useCreateArticle = ({
  refetchArticleData = null,
  handleArticleSelect = () => null,
  isCreatingArticleFromArticle = false,
  setPage = () => null,
  setActiveTab = () => null,
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const {
    actionableArticelId,
    isArticleEditable,
    isDuplicateAction,
    articleDetails,
    articleList,
    showDiscardModal,
    resetFormData,
    isArticleCreateModalOpen,
  } = useSelector((state) => state.articleSlice);

  const { formatedArticleList } = useSelector((state) => state.orderSlice);
  const { refetchBillingData } = useSelector((state) => state.billingSlice);
  const [articleTypeList, setArticleTypeList] = useState([]);
  const [templateList, setTemplateList] = useState([]);
  const [compositionList, setCompositionList] = useState([]);
  const [compositionFormLength, setCompositionFormLength] = useState(1);
  const [washingInstructionsFormLength, setWashingInstructionsLength] = useState(1);
  const [fields, setFields] = useState([{ icon: iconComponents[2].id, description: "" }]);
  const [dropdownOpenIndex, setDropdownOpenIndex] = useState(null);
  const [isIconDropdwonOpen, setIsIconDropdwonOpen] = useState(false);
  const [modifiedGenderList, setModifiedGenderList] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileData, setFileData] = useState([]);
  const [showFileData, setShowFileData] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [hasImage, setHasImage] = useState(false);
  const { showCustomToast } = useCustomToaster();
  const inputRefs = useRef([]);
  const inputWashingRefs = useRef([]);
  const {
    control,
    register,
    reset,
    setValue,
    getValues,
    handleSubmit,
    clearErrors,
    setError,
    setFocus,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      article_title: "",
      article_id: "",
      brand: "",
      sku: "",
      description: "",
      workspace_id: parseInt(workspaceId),
      compositions: [],
      gender_id: null,
      percentage: [],
      article_type: null,
      template_id: null,
      washing_instructions: [{ icon: iconComponents[2].id, description: "" }],
    },
    resolver: zodResolver(createArticleSchema),
  });

  const { data: queryData, isLoading: isArticleTypeListLoading } = useQuery({
    queryKey: ["articleTypes", "compositions", "genders", "template", "getList", workspaceId],
    queryFn: () =>
      Promise.all([
        getArticleTypeList(),
        getCompositionList(),
        getGenders(),
        getTemplates({ pagination: "no", workspace_id: parseInt(workspaceId) }),
      ]),
    enabled: !!workspaceId,
  });

  const { data: articleDetailsById, isPending: isArticleDetailsLoading } = useQuery({
    queryKey: ["article", "getDetails"],
    queryFn: () => getArticleDetailsById(actionableArticelId),
    enabled: !!actionableArticelId,
  });

  const closeModal = () => {
    if (isDuplicateAction) {
      dispatch(toggleDuplicateAction());
    }
    if (isArticleEditable) {
      dispatch(toggleIsArticleEditable());
    }
    if (actionableArticelId) {
      dispatch(toggleActionableArticleId());
    }
    if (isArticleCreateModalOpen) {
      dispatch(toggleIsArticleCreateModalOpen());
    }
    setActiveTab("traceability");
  };

  const createArticleMutation = useMutation({
    mutationKey: ["article", "createArticle"],
    mutationFn: (data) => createArticle(data),
    onMutate: () => {
      showCustomToast(
        {
          title: `${isDuplicateAction ? "Duplicating the article...." : "Creating article ..."}`,
        },
        "createArticleLoadingToast",
      );
    },
    onSuccess: (res) => {
      toast.dismiss("createArticleLoadingToast");
      if (!isCreatingArticleFromArticle && res.data.article) {
        const formatedArticleData = res.data.article;
        const newList = [
          ...formatedArticleList,
          {
            ...formatedArticleData,
            label: formateLable(
              formatedArticleData.article_id,
              formatedArticleData.article_types.name,
            ),
            customLabel: `${formatedArticleData.article_id}${" "}${formatedArticleData.article_types.name}`,
            value: formatedArticleData.id,
            template_id: formatedArticleData.template_id || formatedArticleData?.template?.id,
          },
        ];
        dispatch(setFormatedArticleList(newList));
        dispatch(setShowArticleForm(false));
        handleArticleSelect({
          label: formateLable(
            formatedArticleData?.article_id,
            formatedArticleData?.article_types?.name,
          ),
          customLabel: `${formatedArticleData?.article_id}${" "}${formatedArticleData?.article_types.name}`,
          value: formatedArticleData?.id,
          template_id: formatedArticleData.template_id || formatedArticleData?.template?.id,
        });
      }

      dispatch(setArticleList({ ...res.data, append: true }));

      if (!refetchBillingData) {
        dispatch(setRefetchBillingData(true));
      }
      if (refetchArticleData && typeof refetchArticleData === "function") {
        refetchArticleData();
      }
      closeModal();
      setPage(1);
      reset();
      clearErrors();
      setCompositionFormLength(1);
      setWashingInstructionsLength(1);
      setFileData([]);
      setImagePreviews([]);
      setShowFileData([]);
      setHasImage(false);
      showCustomToast({
        title: `Article created successfully`,
      });
      queryClient.invalidateQueries({ queryKey: ["articles", "createArticle"] });
    },
    onError: (error) => {
      toast.dismiss("createArticleLoadingToast");
      if (error.response?.data?.errors) {
        Object.entries(error.response.data.errors).forEach(([field, messages]) => {
          setError(field, { type: "server", message: messages[0] });
          setFocus(field);
          showCustomToast({
            title: messages[0],
          });
        });
      } else {
        showCustomToast({
          title: error.response?.data?.message || error.message || "Something went wrong",
        });
      }
    },
    onSettled: () => {
      toast.dismiss("createArticleLoadingToast");
      queryClient.invalidateQueries({
        queryKey: ["article", "createArticle"],
      });
      removeQueryParam(
        ["fromGlobalCreate", "fromGlobalSearch", "article_id", "selectedTab"],
        location,
        navigate,
      );
    },
  });

  const updateArticleMutation = useMutation({
    mutationKey: ["article", "updateArticle"],
    mutationFn: (data) => updateArticleDetailsById(data),
    onMutate: () => {
      showCustomToast(
        {
          title: "Upating the Article...",
        },
        "updateArticleLoadingToast",
      );
    },
    onSuccess: (res) => {
      toast.dismiss("updateArticleLoadingToast");
      dispatch(setArticleDetails(res.data.article));
      const updatedArticle = res.data.article;
      const updatedArticles = articleList.map((article) =>
        article.id === updatedArticle.id ? updatedArticle : article,
      );
      if (refetchArticleData && typeof refetchArticleData === "function") {
        refetchArticleData();
      }
      dispatch(setArticleList({ append: false, articleList: updatedArticles }));
      reset();
      closeModal();
      setFileData([]);
      setImagePreviews([]);
      setShowFileData([]);
      setHasImage(false);
      showCustomToast({
        title: "Article updated successfully",
      });
      queryClient.invalidateQueries({ queryKey: ["articles", "updateArticle"] });
    },
    onError: (error) => {
      toast.dismiss();
      showCustomToast({
        title: error.response?.data?.message || error.message || "Something went wrong",
      });
      if (error.response?.data?.errors) {
        Object.entries(error.response.data.errors).forEach(([field, messages]) => {
          setError(field, { type: "server", message: messages[0] });
          setFocus(field);
        });
      }
    },
    onSettled: () => {
      toast.dismiss("updateArticleLoadingToast");
      queryClient.invalidateQueries({
        queryKey: ["article", "updateArticle"],
      });
      removeQueryParam(
        ["fromGlobalCreate", "fromGlobalSearch", "article_id", "selectedTab"],
        location,
        navigate,
      );
    },
  });

  const onSubmit = (data) => {
    const processedData = {
      ...data,
      compositions: data.compositions.map((composition) => composition.value),
      gender_id: data.gender_id?.value,
      article_type: data.article_type?.value,
      template_id: data.template_id?.value,
      main_workspace_id: parseInt(data?.workspace_id),
    };
    const formData = new FormData();
    Object.keys(processedData).forEach((key) => {
      if (key === "washing_instructions") {
        const validWashingInstructions = processedData[key].filter(
          (item) => item.description && item.description.trim() !== "",
        );

        if (validWashingInstructions.length > 0) {
          validWashingInstructions.forEach((item, index) => {
            formData.append(`washing_instructions[${index}][icon]`, item.icon);
            formData.append(`washing_instructions[${index}][description]`, item.description);
          });
        }
      } else if (Array.isArray(processedData[key])) {
        processedData[key].forEach((item, index) => {
          formData.append(`${key}[${index}]`, item);
        });
      } else {
        formData.append(key, processedData[key]);
      }
    });

    if (fileData && fileData.length > 0) {
      fileData.forEach((file, index) => {
        formData.append(`file[${index}]`, file); // Append each file individually with a dynamic key
      });
    }
    if (actionableArticelId) {
      if (hasImage && fileData && fileData.length === 0) {
        const formData = new FormData();
        formData.append(`file[]`, []);
      } else if (fileData && fileData.length > 0) {
        const formData = new FormData();
        fileData.forEach((file, index) => {
          formData.append(`file[${index}]`, file); // Append each file individually with a dynamic key
        });
      }
    }
    if (actionableArticelId && !isDuplicateAction) {
      updateArticleMutation.mutate({ body: formData, id: actionableArticelId });
    } else {
      createArticleMutation.mutate(formData);
    }
  };

  const onValidationError = (error) => {
    console.log(error);
  };

  const handleAddComposition = () => {
    setCompositionFormLength((prev) => prev + 1);
  };

  const handleRemoveComposition = (index) => {
    const compositions = [...getValues().compositions];
    const percentages = [...getValues().percentage];

    if (compositions?.length > 1) {
      compositions.splice(index, 1);
      percentages.splice(index, 1);

      setValue("compositions", compositions);
      setValue("percentage", percentages);
    }
    setCompositionFormLength((prev) => prev - 1);
  };

  const handleWashingInstructionChange = (index, key, value) => {
    const newFields = [...fields];
    newFields[index][key] = value;
    setFields(newFields);
  };

  const handleWashingInstructionKeyDown = (e, index) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      e.stopPropagation();
      if (fields[index].description.trim() !== "") {
        const newFields = [...fields, { icon: iconComponents[2]?.id || "", description: "" }];
        setFields(newFields);
      }
      setTimeout(() => {
        const nextInput = inputWashingRefs.current[index + 1];
        if (nextInput) nextInput.focus();
      }, 0);
    }
  };

  const removeWashingInstructionField = (index) => {
    const newFields = fields.filter((_, i) => i !== index);
    setFields(newFields);
    inputWashingRefs.current.splice(index, 1);
  };

  const selectWashingInstructionIcon = (index, iconId) => {
    handleWashingInstructionChange(index, "icon", iconId);
    setDropdownOpenIndex(null);
    setIsIconDropdwonOpen(false);
  };

  const toggleWashingInstructionDropdown = (index) => {
    setDropdownOpenIndex(dropdownOpenIndex === index ? null : index);
    setIsIconDropdwonOpen((prev) => !prev);
  };

  const toggleEditOrDiscard = () => {
    dispatch(toggleIsArticleEditable());
    if (isArticleEditable) {
      dispatch(toggleShowDiscardModal());
    }
  };

  const handleConfirmDiscard = (confirm) => {
    dispatch(toggleIsArticleEditable());
    if (!confirm) {
      dispatch(toggleShowDiscardModal());
      return;
    }
    dispatch(toggleIsArticleEditable());
    dispatch(toggleShowDiscardModal());
    if (isArticleCreateModalOpen) {
      dispatch(toggleIsArticleCreateModalOpen());
    }
    removeQueryParam(
      ["fromGlobalCreate", "fromGlobalSearch", "article_id", "selectedTab"],
      location,
      navigate,
    );
  };

  const setDefaultFormValue = async (details) => {
    if (!details && !actionableArticelId) return;
    if (details) {
      const {
        sku,
        gender,
        workspace_id,
        article_types,
        compositions,
        percentage,
        template,
        files,
        file,
        article_title,
        article_id,
        brand,
        description,
        washing_instructions,
      } = details;

      if (article_types) {
        setValue("article_type", {
          label: article_types.name,
          value: article_types.id,
        });
      }
      if (washing_instructions?.length > 0) {
        const formattedInstructions = details.washing_instructions.map((item) => ({
          icon: item.icon,
          description: item.description,
        }));
        setFields(formattedInstructions);
        setValue("washing_instructions", formattedInstructions);
      } else {
        setFields([{ icon: iconComponents[2].id, description: "" }]);
        setValue("washing_instructions", [{ icon: iconComponents[2].id, description: "" }]);
      }
      setValue("sku", sku || "");
      setValue("article_title", article_title || "");
      setValue("article_id", article_id);
      setValue("brand", brand || "");
      setValue("description", description || "");

      if (template) {
        setValue("template_id", {
          label: template.name,
          value: parseInt(template.id),
        });
      }
      if (gender) {
        setValue("gender_id", {
          label: gender.name,
          value: parseInt(gender.id),
        });
      }
      setCompositionFormLength(compositions.length);
      setValue(
        "compositions",
        compositions.map(({ name, id }) => ({
          label: name,
          value: id,
        })),
      );
      setValue("workspace_id", parseInt(workspace_id));
      setValue("percentage", percentage);

      if (file && file.length > 0) {
        setImagePreviews(file);
        setValue("file", file);
        setFileData(file);
        if (files && files.length > 0) {
          const transformedFiles = files?.map((file) => ({
            url: file.url,
            name: file.file_name,
            size: file.file_size * 1024,
          }));
          setShowFileData(transformedFiles);
          setHasImage(true);
        }
      } else {
        setImagePreviews([]);
        setFileData([]);
        setShowFileData([]);
        setHasImage(false);
      }
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    onFileDropOrChange(e);
  };

  const onError = (error) => {
    console.log(error);
  };

  const onFileDropOrChange = (e) => {
    const MAX_FILE_SIZE = 10;
    let files = null;
    if (isDragEvent(e)) {
      files = e.dataTransfer.files;
    } else {
      files = e.target.files;
    }

    if (files && files.length > 0) {
      const newFiles = Array.from(files); // Convert FileList to array
      const validFiles = []; // To store valid files only
      const previewPromises = newFiles.map((file, index) => {
        return new Promise((resolve, reject) => {
          // Check file type
          if (file.type !== "application/pdf" && !file.type.startsWith("image/")) {
            showCustomToast({ title: "Please drop a PDF or image file." });
            reject("Invalid file type");
            return;
          }
          // Check file size
          if (file.size > MAX_FILE_SIZE * 1024 * 1024) {
            showCustomToast({ title: "File size exceeds 10MB limit." });
            reject("File size exceeds limit");
            return;
          }
          // Add valid files to the list
          validFiles.push(file);

          // Generate image preview if it's an image file
          if (file.type.startsWith("image/")) {
            const reader = new FileReader();
            reader.onloadend = () => {
              resolve(reader.result);
            };
            reader.onerror = () => {
              reject("Error reading file");
            };
            reader.readAsDataURL(file);
          } else {
            // Resolve with null for non-image files
            resolve(null);
          }
        });
      });

      Promise.all(previewPromises)
        .then((previews) => {
          setValue("file", [...previews]);
          setImagePreviews((prevPreviews) => [...prevPreviews, ...previews]);
          setFileData((prevFileData) => [...prevFileData, ...validFiles]);
          setShowFileData((prevShowFileData) => [...prevShowFileData, ...validFiles]);
          // Clear the input value to allow re-selection of the same files
          if (e.target && e.target.value) {
            e.target.value = null;
          }
        })
        .catch((error) => {
          console.error("Error processing files:", error);
        });
    }
  };

  const handleCertFileDelete = (index) => {
    // Remove the image preview and corresponding file data
    const updatedPreviews = [...imagePreviews];
    const updatedFileData = [...fileData];
    const updatedShowFileData = [...showFileData];
    updatedPreviews.splice(index, 1);
    updatedFileData.splice(index, 1);
    updatedShowFileData.splice(index, 1);
    setImagePreviews(updatedPreviews);
    setFileData(updatedFileData);
    setValue("file", updatedFileData);
    setShowFileData(updatedShowFileData);
  };

  const isDragEvent = (event) => {
    return "dataTransfer" in event;
  };

  const percentageValues = watch("percentage") || [];

  // 👇 Function to add a new composition field
  const addNewCompositionField = () => {
    setCompositionFormLength((prev) => prev + 1);
  };

  const trimFieldsToFitSum = () => {
    const values = getValues("percentage") || [];
    let runningTotal = 0;
    let lastValidIndex = -1;

    for (let i = 0; i < values.length; i++) {
      const val = parseFloat(values[i]) || 0;
      runningTotal += val;
      if (runningTotal <= 100) {
        lastValidIndex = i;
      } else {
        break;
      }
    }

    setCompositionFormLength(lastValidIndex + 1);
  };
  const handleChange = (e, index) => {
    const allValues = getValues("percentage") || [];
    const currentTotal = allValues.reduce(
      (acc, val, i) => acc + (i === index ? 0 : parseFloat(val) || 0),
      0,
    );
    let value = parseFloat(e.target.value) || 0;
    if (value + currentTotal > 100) {
      value = 100 - currentTotal;
    }
    setValue(`percentage.${index}`, value);
  };

  // ✅ Prevent sum from exceeding 100 and auto-trim
  useEffect(() => {
    const total = percentageValues.reduce((acc, val) => acc + (parseFloat(val) || 0), 0);

    if (total >= 100) {
      trimFieldsToFitSum();
    }
  }, [percentageValues]);

  // ✅ On Enter: add a new field if total < 100
  const handleEnterKeyPress = (e, index) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      const total = percentageValues.reduce((acc, val) => acc + (parseFloat(val) || 0), 0);
      if (total < 100) {
        addNewCompositionField();
      }

      setTimeout(() => {
        const nextInput = inputRefs.current[index + 1];
        if (nextInput) nextInput.focus();
      }, 0);
    }
  };

  useEffect(() => {
    if (
      actionableArticelId &&
      articleTypeList &&
      articleDetailsById &&
      articleCompositionsList &&
      articleDetailsById.status === 200
    ) {
      if (articleDetailsById.data) {
        dispatch(setArticleDetails(articleDetailsById.data.article));
        setDefaultFormValue(articleDetailsById.data.article);
      }
    }
  }, [articleDetailsById, actionableArticelId, articleDetails]);

  useEffect(() => {
    const articleTypes = queryData?.[0];
    const compositionData = queryData?.[1];
    const genderData = queryData?.[2];
    const templateData = queryData?.[3];
    if (articleTypes?.data?.article_type) {
      const updatedArticleTypeList = articleTypes.data.article_type.map((item) => ({
        value: item.id,
        label: item.name,
        customLabel: item.name,
      }));
      setArticleTypeList(updatedArticleTypeList);
    }

    if (compositionData?.data?.compositions) {
      const updatedCompositionList = compositionData.data.compositions.map((item) => ({
        value: item.id,
        label: item.name,
        customLabel: item.name,
      }));
      setCompositionList(updatedCompositionList);
    }

    if (genderData?.data?.status && genderData?.data?.gender) {
      const updatedGenderList = genderData.data.gender.map((item) => ({
        value: item.id,
        label: item.name,
        customLabel: item.name,
      }));
      setModifiedGenderList(updatedGenderList);
      dispatch(setGenderList(genderData.data.gender));
    }
    if (templateData?.data?.templates) {
      const updatedtemplatesList = templateData.data.templates.map((item) => ({
        value: item.id,
        label: item.name,
        customLabel: item.name,
      }));
      setTemplateList(updatedtemplatesList);
    }
  }, [queryData]);

  return {
    control,
    isDuplicateAction,
    articleDetails,
    articleTypeList,
    compositionList,
    genderList: modifiedGenderList,
    compositionFormLength,
    errors,
    isLoading: isArticleTypeListLoading,
    isArticleDetailsLoading,
    isPending: createArticleMutation.isPending || updateArticleMutation.isPending,
    isArticleEditable,
    actionableArticelId,
    showDiscardModal,
    register,
    reset,
    handleSubmit,
    onSubmit,
    onValidationError,
    handleAddComposition,
    handleRemoveComposition,
    getValues,
    toggleEditOrDiscard,
    handleConfirmDiscard,
    templateList,
    imagePreviews,
    isDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    onFileDropOrChange,
    handleCertFileDelete,
    fileData,
    handleWashingInstructionKeyDown,
    handleWashingInstructionChange,
    removeWashingInstructionField,
    selectWashingInstructionIcon,
    toggleWashingInstructionDropdown,
    fields,
    dropdownOpenIndex,
    setValue,
    setFileData,
    showFileData,
    handleEnterKeyPress,
    handleChange,
    setIsIconDropdwonOpen,
    isIconDropdwonOpen,
    setDropdownOpenIndex,
    inputRefs,
    inputWashingRefs,
  };
};

export default useCreateArticle;
